import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, SectionTitle } from "@/components/site/kit";
import { Reveal } from "@/components/motion/primitives";
import { getServices } from "@/lib/api";
import { KEYWORDS } from "@/lib/keywords";
import { ArrowUpRight } from "lucide-react";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";

export default function Services() {
  const [services, setServices] = useState([]);
  useEffect(() => { getServices().then(setServices).catch(() => {}); }, []);

  return (
    <div data-testid="services-page">
      <SEO
        title="Web Design, SEO & Website Care Services | Julius Mason"
        description="Web design & development, SEO and search visibility, website care plans, and digital growth services for small businesses worldwide. Transparent pricing, remote delivery."
        path="/services"
        lang="en"
        keywords="web design services, seo services, website maintenance, digital growth, hire web developer"
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
        ])}
      />
      <PageHeader
        chapter="Services / What I Do"
        titleLines={["Websites that", "grow businesses."]}
        description="Web design & development is the core — supported by SEO, website care, and growth services so your site keeps performing long after launch."
      />
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 space-y-4">
          {services.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.06}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-row-${s.slug}`}
                className="group grid md:grid-cols-12 gap-6 items-center border border-border p-8 md:p-10 hover:bg-secondary/40 transition-colors"
              >
                <span className="md:col-span-1 font-mono text-primary text-lg">0{i + 1}</span>
                <div className="md:col-span-4">
                  <h3 className="font-display text-3xl md:text-4xl group-hover:text-primary transition-colors">{s.title}</h3>
                </div>
                <p className="md:col-span-5 text-muted-foreground leading-relaxed">{s.description}</p>
                <div className="md:col-span-2 flex md:justify-end">
                  <span className="inline-flex items-center gap-2 font-medium">Explore <ArrowUpRight className="w-5 h-5 text-primary group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Service-keyword guides — high-intent pages, unique copy each. */}
      <section className="py-16 md:py-24 border-t border-border" data-testid="service-guides">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <SectionTitle overline="Service Guides" title="Find your exact service." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 -mt-4">
            {KEYWORDS.map((k, i) => (
              <Reveal key={k.slug} delay={i * 0.04}>
                <Link to={`/services/${k.slug}`} data-testid={`service-guide-${k.slug}`}
                  className="group flex flex-col border border-border bg-card p-6 h-full hover-lift">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-display text-xl group-hover:text-primary transition-colors pr-4">{k.h1.replace(/\.$/, "")}</h3>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0 mt-1" />
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{k.description}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Related pages — proof + pricing for Services visitors. */}
      <section className="py-16 border-t border-border bg-secondary/30" data-testid="services-related">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <p className="overline text-primary mb-8">See it in action</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { to: "/aveno", label: "AVENO",   desc: "My own everyday-goods brand." },
              { to: "/pricing",   label: "Pricing",     desc: "Transparent packages & care plans." },
              { to: "/web-design", label: "Your Market", desc: "Web design by country, city & industry." },
              { to: "/free-website-audit", label: "Free Audit", desc: "Find out what your current site is costing you." },
            ].map(({ to, label, desc }) => (
              <Link key={to} to={to} data-testid={`services-related-${label.toLowerCase()}`}
                className="group border border-border bg-background p-5 hover-lift flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <span className="font-display text-2xl group-hover:text-primary transition-colors">{label}</span>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                <p className="text-sm text-muted-foreground">{desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
